import React from "react";
import classNames from "classnames";
import quickStatsStyles, { PAGE_WIDTH_MM } from "./QuickStats.styles";

// Team banners: the team color with its contrast color for the text, since a
// team with no color falls back to the theme's white for home
const bannerStyle = (colors) =>
	colors?.background
		? { backgroundColor: colors.background, color: colors.text }
		: undefined;

const PlayerColumn = ({ teamName, colors, sections, side }) => {
	const classes = quickStatsStyles();
	return (
		<div className={classes.column}>
			{sections
				.filter((section) => section[side].length > 0)
				.map((section) => (
					<table
						key={section.key}
						className={classNames(classes.table, classes.playerTable)}
					>
						<thead>
							<tr>
								<th colSpan={section.cols.length} style={bannerStyle(colors)}>
									{`${teamName} - ${section.title}`}
								</th>
							</tr>
							<tr className={classes.columnHead}>
								{section.cols.map((col) => (
									<th key={col}>{col}</th>
								))}
							</tr>
						</thead>
						<tbody>
							{section[side].map((row) => (
								<tr key={row[0]}>
									{row.map((cell, j) => (
										<td key={section.cols[j]}>{cell}</td>
									))}
								</tr>
							))}
						</tbody>
					</table>
				))}
		</div>
	);
};

// `scoreBoard`: the same ScoreBoard header the other football prints open with
const QuickStatsReport = ({
	report,
	awayColors,
	homeColors,
	scoreBoard,
	zoom = 1
}) => {
	const classes = quickStatsStyles();
	const { title, header, lineScore, teamRows, playerSections, scoring } = report;
	const periodKeys = [...lineScore.periods, "T"];

	return (
		// Widen by 1/zoom so the zoomed sheet still spans the full page width;
		// the margin sits outside the zoom so it stays the same when resized
		<div className={classes.sheet}>
			<div
				className={classes.page}
				style={
					zoom !== 1 ? { zoom, width: `${PAGE_WIDTH_MM / zoom}mm` } : undefined
				}
			>
				<div className={classes.title}>{title}</div>
				{scoreBoard}
				<div
					className={classes.meta}
				>{`Generated ${new Date().toLocaleString()}`}</div>

				<table
					className={classNames(classes.table, classes.center, classes.firstLeft)}
				>
					<thead>
						<tr>
							<th>Team</th>
							{lineScore.periods.map((period) => (
								<th key={period}>{period}</th>
							))}
							<th>T</th>
						</tr>
					</thead>
					<tbody>
						<tr>
							<td>{header.awayName}</td>
							{lineScore.away.map((score, i) => (
								<td key={periodKeys[i]}>{score}</td>
							))}
						</tr>
						<tr>
							<td>{header.homeName}</td>
							{lineScore.home.map((score, i) => (
								<td key={periodKeys[i]}>{score}</td>
							))}
						</tr>
					</tbody>
				</table>

				<table className={classNames(classes.table, classes.center)}>
					<thead>
						<tr>
							<th style={bannerStyle(awayColors)}>{header.awayName}</th>
							<th>TEAM STATS</th>
							<th style={bannerStyle(homeColors)}>{header.homeName}</th>
						</tr>
					</thead>
					<tbody>
						{teamRows.map((row) => (
							<tr key={row.label}>
								<td>{row.away}</td>
								<td className={classes.label}>{row.label}</td>
								<td>{row.home}</td>
							</tr>
						))}
					</tbody>
				</table>

				<div className={classes.columns}>
					<PlayerColumn
						teamName={header.awayName}
						colors={awayColors}
						sections={playerSections}
						side="away"
					/>
					<PlayerColumn
						teamName={header.homeName}
						colors={homeColors}
						sections={playerSections}
						side="home"
					/>
				</div>

				{scoring.length > 0 && (
					<table className={classNames(classes.table, classes.scoringTable)}>
						<thead>
							<tr>
								<th>SCORING SUMMARY</th>
								<th>QTR</th>
								<th>CLOCK</th>
								<th>SCORE</th>
							</tr>
						</thead>
						<tbody>
							{scoring.map((score) => (
								<tr key={`${score.period}-${score.clock}-${score.description}`}>
									<td>{score.description}</td>
									<td>{score.period}</td>
									<td>{score.clock}</td>
									<td>{score.score}</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	);
};

export default QuickStatsReport;
